import { useEffect, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import supportClient, { SupportConfig } from "@apis/supportClient";
import type { RootState } from "@/types/RootState";
import { translate } from "@/hooks/useLanguage";
import { LanguageKey } from "@/types/generated/LanguageKey";
import RightSideBar from "@cleanup/widgets/RightSideBar/RightSideBar";
import RightSidebarGroup from "@cleanup/widgets/RightSideBar/RightSidebarGroup";
import HelpSidebarItem from "./HelpSidebarItem";
import * as actions from "@/redux/actions";

import SntDocumentationCenterIcon from "@icons/SntDocumentationCenterIcon";
import SntReleaseNoteIcon from "@icons/SntReleaseNoteIcon";
import SntContactSupportIcon from "@icons/SntContactSupportIcon";
import PartnerIcon from "@images/common/partner.svg";

interface HelpSidebarProps {
  orgId?: number;
}

const HelpSidebar = ({ orgId }: HelpSidebarProps) => {
  const dispatch = useDispatch();
  const loginInfo = useSelector((state: RootState) => state.user, shallowEqual);
  const showHelpSidebar = useSelector(
    (state: RootState) => state.all.showHelpSidebar,
    shallowEqual
  );

  const [support, setSupport] = useState<SupportConfig>({
    helpPageSupportType: "sensolus",
    platformPlansSupportType: "sensolus",
    isHiddenSensolusLink: false,
    helpdeskType: "ZENDESK_SENSOLUS",
    helpdeskBaseUrl: "https://sensolussupport.zendesk.com",
    helpdeskDocUrl: "/rest/authentication/docs-login?redirect=/",
    helpdeskTicketUrl: "https://sensolussupport.zendesk.com/hc/en-us/requests/new",
  });

  useEffect(() => {
    if (orgId) {
      supportClient
        .get(orgId)
        .then(({ data }) => {
          setSupport(data);
        })
        .catch(() => {
          // Keep default state on error
        });
    }
  }, [orgId]);

  const handleClose = () => {
    dispatch(actions.setShowHelpSidebar(false));
  };

  if (!showHelpSidebar) {
    return null;
  }

  const getContactSupportLink = (): string | { pathname: string } => {
    if (loginInfo?.organisation?.organisationType === "PARTNER") {
      return `/zendesk/sso?action=sidebar_click&menu=ticket&useDefaultHelpDesk=true&org=${loginInfo?.organisation.id}`;
    }
    if (support.helpdeskType === "FRESHDESK") {
      return { pathname: support.helpdeskTicketUrl || "" };
    }
    return `/zendesk/sso?action=sidebar_click&menu=ticket&org=${loginInfo?.organisation?.id}`;
  };

  return (
    <RightSideBar
      title={translate(LanguageKey.help_and_support)}
      onClose={handleClose}
      loading={false}
    >
      {/* Documentation Center - Sensolus */}
      {support.helpPageSupportType === "sensolus" && (
        <RightSidebarGroup
          icon={<SntDocumentationCenterIcon size={24} color="#424B59" />}
          title={translate(LanguageKey.documentation_center_key)}
        >
          <HelpSidebarItem
            description={translate(LanguageKey.detail_information_platform_guide)}
            linkText={translate(LanguageKey.documentation_center_guide_key)}
            linkUrl={{ pathname: support.helpdeskDocUrl || "" }}
          />
        </RightSidebarGroup>
      )}

      {/* Release Notes - Sensolus */}
      {support.helpPageSupportType === "sensolus" && (
        <RightSidebarGroup
          icon={<SntReleaseNoteIcon size={24} color="#424B59" />}
          title={translate(LanguageKey.release_notes)}
          showDivider
        >
          <HelpSidebarItem
            description={translate(LanguageKey.find_out_whats_new_in_the_platform)}
            linkText={translate(LanguageKey.read_the_release_notes)}
            linkUrl="/rest/authentication/docs-login?redirect=/docs/releases/release_notes"
          />
        </RightSidebarGroup>
      )}

      {/* Custom Help Page */}
      {support.helpPageSupportType === "custom" && (
        <RightSidebarGroup
          icon={<SntDocumentationCenterIcon size={24} color="#424B59" />}
          title={support.helpPageTitle || ""}
        >
          <HelpSidebarItem htmlContent={support.helpPageDescription || ""} />
        </RightSidebarGroup>
      )}

      {/* Partner Portal */}
      {loginInfo?.organisation?.organisationType === "PARTNER" &&
        loginInfo?.partner?.commercialPartner && (
          <RightSidebarGroup
            icon={<img src={PartnerIcon} width="24" height="24" alt="" />}
            title={translate(LanguageKey.partner_portal_key)}
            showDivider
          >
            <HelpSidebarItem
              description={translate(LanguageKey.partner_portal_description_key)}
              linkText={translate(LanguageKey.visit_the_partner_portal_key)}
              linkUrl="/rest/authentication/docs-login?redirect=/docs/partner/partner_resource_center"
            />
          </RightSidebarGroup>
        )}

      {/* Contact Support - Custom */}
      {support.helpdeskType === "CUSTOM_SUPPORT" ? (
        <RightSidebarGroup
          icon={<SntContactSupportIcon size={24} color="#424B59" />}
          title={support.helpdeskTitle || ""}
          showDivider
        >
          <HelpSidebarItem htmlContent={support.helpdeskDescription || ""} />
        </RightSidebarGroup>
      ) : (
        <RightSidebarGroup
          icon={<SntContactSupportIcon size={24} color="#424B59" />}
          title={translate(LanguageKey.contact_support_key)}
          showDivider
        >
          <HelpSidebarItem
            description={translate(LanguageKey.do_you_have_questions_remarks)}
            linkText={translate(LanguageKey.make_a_request)}
            linkUrl={getContactSupportLink()}
          />
        </RightSidebarGroup>
      )}
    </RightSideBar>
  );
};

export default HelpSidebar;
